import { Injectable, computed, inject } from "@angular/core";
import { GridItemType, Comment, Trade } from "@app/common/types/platform.types";
import { ChartData } from "@app/common/charts/services/chart.service";
import { EChartsCoreOption } from "echarts/core";
import { USER_NOTES_ID } from "./dashboard-state.service";

// Constants for chart IDs from DashboardComponent (ensure these are kept in sync or managed globally)
const SYMBOL_TRADE_ALLOCATION_PIE_CHART_ID = "symbolTradeAllocationPie";
const WIN_RATE_HORIZONTAL_BAR_CHART_ID = "winRateHorizontalBarChart";
// STANDALONE_ECHART_TYPE is a type, not an ID, so it's not directly used for summarization mapping here unless a specific ID uses it.

type Summarizer = (data: any) => string;

@Injectable({
  providedIn: "root",
})
export class SummarizationService {
  private summarizers = new Map<string, Summarizer>();

  constructor() {
    this.registerSummarizers();
  }

  private registerSummarizers(): void {
    this.summarizers.set(
      GridItemType.ProfitLossByUnderlying,
      this.summarizeProfitLossByUnderlying.bind(this)
    );
    this.summarizers.set(
      GridItemType.ProfitLossOverTime,
      this.summarizeProfitLossOverTime.bind(this)
    );
    this.summarizers.set(
      GridItemType.ProfitLossByExpiration,
      this.summarizeProfitLossByExpiration.bind(this)
    );
    this.summarizers.set(
      GridItemType.ProfitLossByTimePeriod,
      this.summarizeProfitLossByTimePeriod.bind(this)
    );
    this.summarizers.set(
      GridItemType.UnderlyingTimeline,
      this.summarizeUnderlyingTimeline.bind(this)
    );
    this.summarizers.set(
      GridItemType.ProfitLossByStrategy,
      this.summarizeProfitLossByStrategy.bind(this)
    );
    this.summarizers.set(
      SYMBOL_TRADE_ALLOCATION_PIE_CHART_ID,
      this.summarizeSymbolTradeAllocationPie.bind(this)
    );
    this.summarizers.set(
      WIN_RATE_HORIZONTAL_BAR_CHART_ID,
      this.summarizeWinRateHorizontalBar.bind(this)
    );
    this.summarizers.set(USER_NOTES_ID, this.summarizeUserNotes.bind(this));
    this.summarizers.set(
      GridItemType.AllTrades,
      this.summarizeAllTrades.bind(this)
    );
    // ChartType.TradeStats might need a specific summarizer if its data structure is unique
    // this.summarizers.set(ChartType.TradeStats, this.summarizeTradeStats.bind(this));
  }

  public summarize(id: string, data: any): string {
    if (!data) {
      return ""; // Or a specific message like "Data not available for [id]"
    }
    const summarizer = this.summarizers.get(id);
    if (summarizer) {
      try {
        return summarizer(data);
      } catch (error) {
        console.error(`Error summarizing ${id}:`, error, data);
        return `Error summarizing data for ID ${id}.`;
      }
    }
    return `No summarization available for ID ${id}.`;
  }

  // --- Individual Summarization Functions ---

  private summarizeAllTrades(trades: Trade[]): string {
    if (!trades || trades.length === 0) {
      return "All Trades: No trades available to summarize.";
    }

    // Sort trades by openDate ascending
    const sortedTrades = [...trades].sort((a, b) => {
      const dateA = a.openDate
        ? new Date(a.openDate).getTime()
        : new Date(a.date).getTime();
      const dateB = b.openDate
        ? new Date(b.openDate).getTime()
        : new Date(b.date).getTime();
      return dateA - dateB;
    });

    let summary = "Summary of All Trades (sorted by open date):\n";
    const tradeDetails: string[] = sortedTrades.map((trade, index) => {
      let detail = `Trade ${index + 1}:\n`;
      for (const key in trade) {
        if (Object.prototype.hasOwnProperty.call(trade, key)) {
          const value = trade[key as keyof Trade];
          detail += `  - ${key}: ${
            value === undefined || value === null
              ? "N/A"
              : JSON.stringify(value)
          }\n`;
        }
      }
      return detail;
    });

    summary += tradeDetails.join("\n");
    return summary;
  }

  private summarizeProfitLossByUnderlying(data: ChartData): string {
    if (
      !data ||
      !data.xAxis ||
      !Array.isArray(data.xAxis) ||
      !data.series ||
      !Array.isArray(data.series) ||
      data.series.length === 0
    ) {
      return "Profit/Loss by Underlying: Data is incomplete or unavailable.";
    }
    const seriesData = data.series[0].data as Array<{
      value: number;
      itemStyle?: any;
      label?: any;
    }>;
    if (!seriesData || !Array.isArray(seriesData)) {
      return "Profit/Loss by Underlying: Series data is incomplete or unavailable.";
    }

    let summary = "Profit/Loss by Underlying Symbol:\n";
    let totalPL = 0;
    const details: string[] = [];

    data.xAxis.forEach((symbol, index) => {
      const plData = seriesData[index];
      if (plData && typeof plData.value === "number") {
        const plValue = plData.value;
        totalPL += plValue;
        details.push(
          `- ${symbol}: ${plValue >= 0 ? "+" : ""}$${plValue.toLocaleString(
            undefined,
            { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          )}`
        );
      }
    });

    if (details.length === 0) {
      return "Profit/Loss by Underlying: No profit/loss data points found.";
    }

    summary += details.join("\n");
    summary += `\nOverall P/L from these symbols: $${totalPL.toLocaleString(
      undefined,
      { minimumFractionDigits: 0, maximumFractionDigits: 0 }
    )}`;
    return summary;
  }

  private summarizeSymbolTradeAllocationPie(data: ChartData): string {
    if (
      !data ||
      !data.series ||
      !Array.isArray(data.series) ||
      data.series.length === 0
    ) {
      return "Symbol Trade Allocation: Data is incomplete or unavailable.";
    }
    const pieSeries = data.series.find((s) => s.type === "pie");
    if (!pieSeries || !pieSeries.data || !Array.isArray(pieSeries.data)) {
      return "Symbol Trade Allocation: Pie series data is incomplete or unavailable.";
    }

    const seriesData = pieSeries.data as Array<{ name: string; value: number }>;
    let summary = "Trade Allocation by Underlying (trade count):\n";
    const details: string[] = [];
    let totalTrades = 0;

    seriesData.forEach((item) => {
      totalTrades += item.value;
    });

    // Safely access title text, assuming it might be on `data` as part of ECharts options
    const chartTitleObject = (data as any).title; // Cast to any for flexible access
    const chartTitleText =
      chartTitleObject &&
      !Array.isArray(chartTitleObject) &&
      chartTitleObject.text
        ? chartTitleObject.text
        : "";

    if (
      totalTrades === 0 &&
      pieSeries.name === "Trade Allocation" &&
      chartTitleText.includes("No trades")
    ) {
      return `Underlying Trade Allocation: ${chartTitleText}`;
    }

    if (seriesData.length === 0) {
      return "Underlying Trade Allocation: No allocation data points found.";
    }

    seriesData.forEach((item) => {
      const percentage =
        totalTrades > 0 ? ((item.value / totalTrades) * 100).toFixed(1) : "0.0";
      details.push(`- ${item.name}: ${item.value} trades (${percentage}%)`);
    });

    summary += details.join("\n");
    summary += `\nTotal trades considered for allocation: ${totalTrades}.`;
    if (seriesData.length > 0) {
      summary += ` ${seriesData[0].name} had the highest number of trades.`;
    }
    return summary;
  }

  private summarizeProfitLossOverTime(data: ChartData): string {
    if (
      !data ||
      !data.xAxis ||
      !Array.isArray(data.xAxis) ||
      data.xAxis.length === 0 ||
      !data.series ||
      !Array.isArray(data.series) ||
      data.series.length === 0
    ) {
      return "Profit/Loss Over Time: Data is incomplete or unavailable.";
    }

    let summary = "Profit/Loss Over Time (Cumulative):\n";
    const seriesDetails: string[] = [];

    data.series.forEach((s) => {
      const seriesName = s.name || "Unnamed Series";
      const seriesData = s.data as Array<{ value: number } | number>; // Can be array of objects or just numbers
      if (!seriesData || seriesData.length === 0) return;

      const firstPoint = seriesData[0];
      const lastPoint = seriesData[seriesData.length - 1];

      const firstValue =
        typeof firstPoint === "number"
          ? firstPoint
          : (firstPoint as { value: number }).value;
      const lastValue =
        typeof lastPoint === "number"
          ? lastPoint
          : (lastPoint as { value: number }).value;

      const startDate = data.xAxis![0] as string;
      const endDate = data.xAxis![data.xAxis!.length - 1] as string;

      seriesDetails.push(
        `- Series '${seriesName}': Started at $${firstValue?.toLocaleString(
          undefined,
          { minimumFractionDigits: 0, maximumFractionDigits: 0 }
        )} on ${startDate}, ended at $${lastValue?.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })} on ${endDate}.`
      );
      seriesDetails.push(
        `  Net change for '${seriesName}': $${(
          lastValue - firstValue
        ).toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}.`
      );
    });

    if (seriesDetails.length === 0) {
      return "Profit/Loss Over Time: No time series data points found.";
    }
    summary += seriesDetails.join("\n");
    return summary;
  }

  private summarizeProfitLossByExpiration(data: ChartData): string {
    if (
      !data ||
      !data.xAxis ||
      !Array.isArray(data.xAxis) ||
      !data.series ||
      !Array.isArray(data.series) ||
      data.series.length === 0
    ) {
      return "Profit/Loss by Expiration Range: Data is incomplete or unavailable.";
    }
    const seriesData = data.series[0].data as Array<{
      value: number;
      count?: number;
      underlyingSymbolCounts?: Array<[string, number]>;
    }>;
    if (!seriesData || !Array.isArray(seriesData)) {
      return "Profit/Loss by Expiration Range: Series data is incomplete or unavailable.";
    }

    let summary = "Profit/Loss by Expiration Range (DTE at Open):\n";
    const details: string[] = [];

    data.xAxis.forEach((rangeName, index) => {
      const rangeData = seriesData[index];
      if (rangeData && typeof rangeData.value === "number") {
        const plValue = rangeData.value;
        const tradeCount = rangeData.count || "N/A";
        let detail = `- ${rangeName}: P/L $${plValue.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}, Trades: ${tradeCount}`;
        if (
          rangeData.underlyingSymbolCounts &&
          rangeData.underlyingSymbolCounts.length > 0
        ) {
          const topUnderlyings = rangeData.underlyingSymbolCounts
            .slice(0, 3)
            .map((uc) => `${uc[0]} (${uc[1]})`)
            .join(", ");
          detail += ` (Top underlyings: ${topUnderlyings})`;
        }
        details.push(detail);
      }
    });

    if (details.length === 0) {
      return "Profit/Loss by Expiration Range: No data points found.";
    }

    summary += details.join("\n");
    return summary;
  }

  private summarizeProfitLossByTimePeriod(data: ChartData): string {
    if (
      !data ||
      !data.xAxis ||
      !Array.isArray(data.xAxis) ||
      !data.series ||
      !Array.isArray(data.series) ||
      data.series.length === 0
    ) {
      return "Profit/Loss by Time Period: Data is incomplete or unavailable.";
    }
    const seriesData = data.series[0].data as Array<{
      value: number;
      label?: { formatter: (params: any) => string };
    }>; // Assuming label.formatter might have trade count
    const timePeriodName = data.series[0].name || "Period"; // e.g., "Daily P/L"
    if (!seriesData || !Array.isArray(seriesData)) {
      return `Profit/Loss by Time Period (${timePeriodName}): Series data is incomplete or unavailable.`;
    }

    let summary = `Profit/Loss by Time Period (${timePeriodName}):\n`;
    const details: string[] = [];

    data.xAxis.forEach((period, index) => {
      const periodData = seriesData[index];
      if (periodData && typeof periodData.value === "number") {
        const plValue = periodData.value;
        // Attempt to extract trade count if available from label formatter
        let tradeCountStr = "";
        if (periodData.label && periodData.label.formatter) {
          try {
            const labelText = periodData.label.formatter({
              value: plValue,
              dataIndex: index,
              seriesIndex: 0,
              name: period,
            } as any);
            const match = labelText.match(/\((\d+) trades\)/);
            if (match && match[1]) {
              tradeCountStr = `, Trades: ${match[1]}`;
            }
          } catch (e) {
            /* Trade count not parsable */
          }
        }
        details.push(
          `- ${period}: P/L $${plValue.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}${tradeCountStr}`
        );
      }
    });

    if (details.length === 0) {
      return `Profit/Loss by Time Period (${timePeriodName}): No data points found.`;
    }

    summary += details.join("\n");
    return summary;
  }

  private summarizeUnderlyingTimeline(data: ChartData): string {
    // This chart can be complex: EOD prices, cumulative P/L, or trade transactions.
    // The summarization needs to adapt based on what's being shown.
    // The `data` object might not directly tell us the view type, so this might need enhancement
    // or an additional parameter if the AIService can determine the current timeline view.

    if (
      !data ||
      !data.series ||
      !Array.isArray(data.series) ||
      data.series.length === 0
    ) {
      return "Underlying Timeline: Data is incomplete or unavailable.";
    }

    const firstSeries = data.series[0];
    const seriesName = firstSeries.name || "Timeline Data";
    const xAxisData = (data.xAxis as string[]) || [];
    const seriesValues = (firstSeries.data as Array<{ value: any } | any>)?.map(
      (d) => (typeof d === "object" && d !== null && "value" in d ? d.value : d)
    );

    if (xAxisData.length === 0 || !seriesValues || seriesValues.length === 0) {
      return `Underlying Timeline (${seriesName}): No data points found.`;
    }

    const startDate = xAxisData[0];
    const endDate = xAxisData[xAxisData.length - 1];
    const startValue = seriesValues[0];
    const endValue = seriesValues[seriesValues.length - 1];

    let summary = `Underlying Timeline - ${seriesName}:\n`;
    summary += `- Period: ${startDate} to ${endDate}\n`;

    if (seriesName.toLowerCase().includes("price")) {
      summary += `- Starting Price: $${Number(startValue).toLocaleString(
        undefined,
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}\n`;
      summary += `- Ending Price: $${Number(endValue).toLocaleString(
        undefined,
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}\n`;
      const change = Number(endValue) - Number(startValue);
      const pctChange = (change / Number(startValue)) * 100;
      summary += `- Price Change: $${change.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} (${pctChange.toFixed(2)}%)\n`;
    } else if (
      seriesName.toLowerCase().includes("p/l") ||
      seriesName.toLowerCase().includes("performance")
    ) {
      summary += `- Starting Cumulative P/L: $${Number(
        startValue
      ).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}\n`;
      summary += `- Ending Cumulative P/L: $${Number(endValue).toLocaleString(
        undefined,
        { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      )}\n`;
      summary += `- Net P/L Change over period: $${(
        Number(endValue) - Number(startValue)
      ).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}\n`;
    }

    // Check for trade transaction markers (scatter series)
    const scatterSeries = data.series.find(
      (s) => s.type === "scatter" && s.name?.toLowerCase().includes("trade")
    );
    if (scatterSeries && Array.isArray(scatterSeries.data)) {
      const tradeMarkers = scatterSeries.data as Array<{
        value: [number, number];
        trades?: any[];
        dailyProfitLossSum?: number;
        comments?: number;
      }>;
      const tradesWithPL = tradeMarkers.filter(
        (t) => t.dailyProfitLossSum !== undefined && t.dailyProfitLossSum !== 0
      );
      if (tradesWithPL.length > 0) {
        summary += `\nKey Trading Activity Markers:\n`;
        const topN = 3;
        tradesWithPL
          .sort(
            (a, b) =>
              Math.abs(b.dailyProfitLossSum!) - Math.abs(a.dailyProfitLossSum!)
          )
          .slice(0, topN)
          .forEach((tradeMarker) => {
            const date = xAxisData[tradeMarker.value[0]];
            const pl = tradeMarker.dailyProfitLossSum!;
            const numTrades = tradeMarker.trades?.length || "multiple";
            summary += `- On ${date}: Day P/L $${pl.toLocaleString(undefined, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })} (${numTrades} trades)${
              tradeMarker.comments ? ", Notes: " + tradeMarker.comments : ""
            }\n`;
          });
        if (tradesWithPL.length > topN)
          summary += `- ...and ${
            tradesWithPL.length - topN
          } other trading days with P/L.\n`;
      }
    }
    return summary;
  }

  private summarizeProfitLossByStrategy(data: ChartData): string {
    if (
      !data ||
      !data.xAxis ||
      !Array.isArray(data.xAxis) ||
      !data.series ||
      !Array.isArray(data.series) ||
      data.series.length === 0
    ) {
      return "Profit/Loss by Strategy: Data is incomplete or unavailable.";
    }

    const seriesType = data.series[0].type; // 'bar' or 'line'

    if (seriesType === "bar") {
      const seriesData = data.series[0].data as Array<{
        value: number;
        strategyType?: string;
      }>;
      if (!seriesData || !Array.isArray(seriesData)) {
        return "Profit/Loss by Strategy (Bar Chart): Series data is incomplete or unavailable.";
      }
      let summary = "Profit/Loss by Strategy (Bar Chart):\n";
      const details: string[] = [];
      data.xAxis.forEach((strategyName, index) => {
        const strategyData = seriesData[index];
        if (strategyData && typeof strategyData.value === "number") {
          details.push(
            `- ${strategyName}: P/L $${strategyData.value.toLocaleString(
              undefined,
              { minimumFractionDigits: 0, maximumFractionDigits: 0 }
            )}`
          );
        }
      });
      if (details.length === 0)
        return "Profit/Loss by Strategy (Bar Chart): No data points found.";
      summary += details.join("\n");
      return summary;
    } else if (seriesType === "line") {
      let summary =
        "Profit/Loss by Strategy (Line Chart - Cumulative P/L Over Time):\n";
      const seriesDetails: string[] = [];
      const xAxisData = (data.xAxis as string[]) || [];
      if (xAxisData.length === 0)
        return "Profit/Loss by Strategy (Line Chart): X-axis data (dates) unavailable.";

      data.series.forEach((s) => {
        const strategyName = s.name || "Unnamed Strategy";
        const strategySeriesData = s.data as Array<{ value: number } | number>;
        if (!strategySeriesData || strategySeriesData.length === 0) return;

        const firstPoint = strategySeriesData[0];
        const lastPoint = strategySeriesData[strategySeriesData.length - 1];
        const firstValue =
          typeof firstPoint === "number"
            ? firstPoint
            : (firstPoint as { value: number }).value;
        const lastValue =
          typeof lastPoint === "number"
            ? lastPoint
            : (lastPoint as { value: number }).value;
        const startDate = xAxisData[0];
        const endDate = xAxisData[xAxisData.length - 1];

        seriesDetails.push(
          `- Strategy '${strategyName}': Cumulative P/L from $${firstValue?.toLocaleString(
            undefined,
            { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          )} on ${startDate} to $${lastValue?.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })} on ${endDate}.`
        );
      });
      if (seriesDetails.length === 0)
        return "Profit/Loss by Strategy (Line Chart): No series data found.";
      summary += seriesDetails.join("\n");
      return summary;
    }
    return `Profit/Loss by Strategy: Unknown series type '${seriesType}'.`;
  }

  private summarizeWinRateHorizontalBar(data: EChartsCoreOption): string {
    if (
      !data ||
      !data["series"] ||
      !Array.isArray(data["series"]) ||
      data["series"].length === 0 ||
      !data["yAxis"]
    ) {
      return "Underlying Efficiency Score: Data is incomplete or unavailable.";
    }
    const yAxis = Array.isArray(data["yAxis"])
      ? data["yAxis"][0]
      : data["yAxis"];
    const underlyingNames = ((yAxis as any)?.data as string[]) || []; // Cast yAxis to any to access data
    const series = data["series"][0] as any; // Cast series to any
    const seriesData =
      (series?.data as Array<{
        value: number;
        winRate: number;
        wins: number;
        total: number;
        totalProfit: number;
        avgProfit: number;
      }>) || [];

    if (underlyingNames.length === 0 || seriesData.length === 0) {
      return "Underlying Efficiency Score: No data points found.";
    }

    const titleOption = Array.isArray(data["title"])
      ? data["title"][0]
      : data["title"];
    const titleText =
      titleOption && !Array.isArray(titleOption) && (titleOption as any).text
        ? (titleOption as any).text
        : "";
    const profitWeight =
      titleText.match(/Profit Weight: (\d+)/)?.[1] || "current";

    let summary = `Underlying Efficiency Score (Profit Weight: ${profitWeight}):\nSorted by Efficiency Score (descending).\n`;
    const details: string[] = [];

    underlyingNames.forEach((underlying, index) => {
      const stats =
        seriesData.find((s) => (s as any).name === underlying) ||
        seriesData[index]; // find by name or by index
      if (stats) {
        details.push(
          `- ${underlying}: Efficiency Score ${stats.value.toFixed(
            2
          )}, Win Rate ${(stats.winRate || 0).toFixed(0)}% (${stats.wins}/${
            stats.total
          } trades), Total P/L $${(stats.totalProfit || 0).toLocaleString(
            undefined,
            { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          )}, Avg P/L $${(stats.avgProfit || 0).toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}`
        );
      }
    });

    if (details.length === 0) {
      return "Underlying Efficiency Score: No underlying data found after processing.";
    }
    summary += details.join("\n");
    return summary;
  }

  private summarizeUserNotes(notes: Comment[]): string {
    if (!notes || notes.length === 0) {
      return "User Notes: No notes available.";
    }

    let summary = "User Notes Summary:\n";
    const noteDetails: string[] = [];
    const maxNotesToSummarize = 5; // Limit the number of notes in the summary to keep it concise

    notes.slice(0, maxNotesToSummarize).forEach((note, index) => {
      const noteDate = note.dateLabel
        ? ` on ${note.dateLabel}`
        : note.timestamp
        ? ` on ${new Date(note.timestamp).toLocaleDateString()}`
        : "";
      const symbols =
        note.symbols && note.symbols.length > 0
          ? ` (Symbols: ${note.symbols.join(", ")})`
          : "";
      noteDetails.push(
        `- Note ${index + 1}${noteDate}${symbols}: ${note.text}`
      );
    });

    summary += noteDetails.join("\n");

    if (notes.length > maxNotesToSummarize) {
      summary += `\n... and ${notes.length - maxNotesToSummarize} more notes.`;
    }
    return summary;
  }

  // private summarizeTradeStats(data: any): string {
  //   // Assuming data for TradeStats is a simple object with key-value pairs
  //   if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
  //     return "Trade Statistics: Data is incomplete or unavailable.";
  //   }
  //   let summary = "Key Trade Statistics:\n";
  //   for (const [key, value] of Object.entries(data)) {
  //     const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()); // Convert camelCase to Title Case
  //     summary += `- ${formattedKey}: ${value}\n`;
  //   }
  //   return summary;
  // }
}
